import { Cable, DataSaverOff, Shower, WaterDrop } from '@mui/icons-material';
import { Card, Divider, Grid2, List, styled, Typography, useTheme } from '@mui/material';
import {
  BackboardIcon,
  CouplerIcon,
  FobIcon,
  GlassWasherIcon,
  GlasswareIcon,
  NozzlesIcon,
  RemoteGrilleIcon,
  SocketsIcon,
  TapIcon,
  TempCellarIcon
} from 'assets/custom_icons/customIcons';
import { type FC, useEffect, useRef } from 'react';
import type { HygieneItemType, HygieneStatusElement, IIsPdf } from '../../../models';
import { ImageViewer } from '../../Dialogs';
import { AvaniScrollable } from '../../Utils';
import { AvaniHygieneBarCard } from './AvaniHygieneBarCard';
import { AvaniHygieneTrendsCard } from './AvaniHygieneTrendsCard';

interface AvaniHygieneCardProps extends IIsPdf {
  data: HygieneStatusElement;
}

const AvaniStyledCard = styled(Card)(
  () => `
  border-radius: 16px;
  padding: 16px;
  width: 409px;
  min-height: 400px;
  display: flex;
  float: left;
  flex-direction: column;
  justify-content: space-between;
  margin: 0 9px 9px 0;

  .MuiTypography-root.MuiTypography-h3 {
    margin: 0;
    line-height: 1;
    padding-bottom: 12px;
  }
`
);

const getHeadingIcon = (
  category: HygieneItemType,
  headingIconDimmensions: {
    width: number;
    height: number;
  },
  isPDF: boolean,
  iconSx: { fontSize: string }
) => {
  switch (category) {
    case 'Nozzles':
      return NozzlesIcon({ ...headingIconDimmensions, isPDF });
    case 'Glassware':
      return GlasswareIcon({ ...headingIconDimmensions, isPDF });
    case 'Glass Washers':
      return GlassWasherIcon({ ...headingIconDimmensions, isPDF });
    case 'FOBs':
      return FobIcon({ ...headingIconDimmensions, isPDF });
    case 'Flush Water Clear':
      return <WaterDrop color={'aTeal'} fontSize="large" sx={{ iconSx }} />;
    case 'Sockets':
      return SocketsIcon({ ...headingIconDimmensions, isPDF });
    case 'Couplers':
      return CouplerIcon({ ...headingIconDimmensions, isPDF });
    case 'Tap Seals':
      return <DataSaverOff color={'aTeal'} fontSize="large" sx={{ iconSx }} />;
    case 'Remote Grille':
      return RemoteGrilleIcon({ ...headingIconDimmensions, isPDF });
    case 'Python Condition':
      return <Cable color={'aTeal'} fontSize="large" sx={{ iconSx }} />;
    case 'Back Board':
      return BackboardIcon({ ...headingIconDimmensions, isPDF });
    case 'Cask Equipment':
      return TempCellarIcon({ ...headingIconDimmensions, isPDF });
    case 'Tap Hygiene':
      return TapIcon({ ...headingIconDimmensions, isPDF });
    default:
      return <Shower fontSize="large" sx={{ iconSx }} />;
  }
};

export const AvaniHygieneCard: FC<AvaniHygieneCardProps> = ({ data, isPDF = false }) => {
  const theme = useTheme();
  const { category, roomType, hygieneStatusVisitDatesTrend, rooms } = data;

  // Extract current Hygiene Check medias (limit to 3) for PDF
  const pdfMedias = rooms.flatMap(({ media }) => media).slice(0, 3);

  const descriptionEl = useRef<HTMLElement>(null);
  const descriptionFrameEl = useRef<HTMLElement>(null);

  const iconSx = { fontSize: '32px' };
  const headingIconDimmensions = { width: 42, height: 42 };
  const headingIcon = getHeadingIcon(category, headingIconDimmensions, isPDF, iconSx);
  const failedHygiene = rooms.some(({ hygieneStatus }) => hygieneStatus === false);
  const subtitle = `Results by ${roomType}`;

  useEffect(() => {
    if (descriptionEl.current && descriptionEl.current.clientHeight > 60) {
      descriptionFrameEl.current?.classList.add('long');
    }
  }, [descriptionEl, descriptionFrameEl]);

  return (
    <AvaniStyledCard
      raised={true}
      sx={{ backgroundColor: failedHygiene ? theme.colors.avani.lightMagenta : theme.colors.avani.lightTurc }}
    >
      <div style={{ display: 'flex' }}>
        <Typography variant={'h3'} color={theme.colors.avani.teal} sx={{ flexGrow: 1 }}>
          {category}
        </Typography>
        <span>{headingIcon}</span>
      </div>
      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <div>
          <Typography variant={'h5'} fontSize={18} color={'primary'} sx={{ paddingBottom: '10px' }}>
            {subtitle}
          </Typography>
          <div style={{ position: 'relative', width: '100%', minHeight: '60px', overflowX: 'auto' }}>
            {!!rooms.length && (
              <AvaniScrollable height={100} horizontal sx={{ display: 'grid', alignItems: 'center' }}>
                <List disablePadding sx={{ width: 'max-content', float: 'left', display: 'block' }}>
                  {rooms.map((room, ind) => {
                    return (
                      <AvaniHygieneBarCard key={ind} room={room} roomType={roomType} category={category} index={ind} />
                    );
                  })}
                </List>
              </AvaniScrollable>
            )}
          </div>
        </div>
        <div>
          <Divider
            sx={{
              borderBottom: `1px dashed ${theme.colors.avani.turc}`,
              backgroundColor: 'transparent',
              display: 'block',
              width: '100%'
            }}
          />
          <Typography variant={'h5'} fontSize={18} color={'primary'} sx={{ paddingY: '10px' }}>
            {category} Hygiene Trend
          </Typography>
          <div style={{ position: 'relative', width: '100%', minHeight: '60px', overflow: 'auto' }}>
            <AvaniScrollable height={100} horizontal sx={{ display: 'grid', alignItems: 'center' }}>
              <List disablePadding sx={{ width: 'max-content', float: 'left', display: 'block' }}>
                {hygieneStatusVisitDatesTrend.map((trend, ind) => {
                  return <AvaniHygieneTrendsCard key={ind} trend={trend} category={category} index={ind} />;
                })}
              </List>
            </AvaniScrollable>
          </div>
        </div>
        {isPDF && (
          <div>
            <Divider
              sx={{
                borderBottom: `1px dashed ${theme.colors.avani.turc}`,
                backgroundColor: 'transparent',
                display: 'block',
                width: '100%'
              }}
            />
            <Typography variant={'h5'} fontSize={18} color={'primary'} sx={{ paddingY: '10px' }}>
              {category} Hygiene Photos
            </Typography>
            <Grid2 container sx={{ marginTop: 2, minHeight: '100px', justifyContent: 'flex-start', gap: 2 }}>
              {pdfMedias.map(({ contentUrl }, i) => {
                return (
                  <Grid2 key={i}>
                    <ImageViewer
                      key={contentUrl}
                      index={i}
                      imageSrc={pdfMedias}
                      avatarWidth={100}
                      avatarHeight={100}
                      avatarSx={{ borderRadius: 1 }}
                      isPDF
                    />
                  </Grid2>
                );
              })}
            </Grid2>
          </div>
        )}
      </div>
    </AvaniStyledCard>
  );
};
